import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { AuditLogDto } from "@/features/admin/api/dto";

/**
 * Colonnes du journal d'audit consolidé (`POST /AuditLogs/all`) — les
 * filtres (service, ressource, acteur) sont pilotés par la barre externe
 * `AuditLogsFilters` (mode server) : les champs DataTableClar sont masqués
 * (`denied`) pour ne pas simuler un filtre non câblé.
 * Identifiants, IP et actions en `NoTranslate`.
 */
export const auditLogsColumns: ColumnsClarFactory<AuditLogDto> = () => [
  {
    id: "occurredAt",
    accessorKey: "occurredAt",
    header: t("admin.audit.columns.occurredAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "action",
    accessorKey: "action",
    header: t("admin.audit.columns.action"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "resource",
    accessorFn: (row) => `${row.resourceType} ${row.resourceId}`,
    header: t("admin.audit.columns.resourceType"),
    cell: ({ row }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {row.original.resourceType} · {row.original.resourceId.slice(0, 8)}…
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "sourceService",
    accessorKey: "sourceService",
    header: t("admin.audit.columns.sourceService"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actorKeycloakId",
    accessorKey: "actorKeycloakId",
    header: t("admin.audit.columns.actor"),
    cell: ({ getValue }) => {
      const id = getValue() as string | undefined | null;
      return (
        <NoTranslate as="span" className="font-mono text-xs">
          {id ? `${id.slice(0, 8)}…` : "—"}
        </NoTranslate>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "ipAddress",
    accessorKey: "ipAddress",
    header: t("admin.audit.columns.ipAddress"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string | null | undefined) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
];
